import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, CheckCircle2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { guardarDatoCabecera, tarjetaMarca, type ResultadoGuardar } from "@/lib/marca/guardarDato";
import { buildActionUrl, getAssistantAction } from "@/lib/assistantActions";
import { AssistantActionButtons, type AssistantActionItem } from "@/components/assistant/AssistantActionButtons";

type Estado = "idle" | "saving" | "saved" | "discarded" | "error";

/**
 * «Guardar en la cabecera» (F4 de «Personalizar informes», R10, 29-09-2026).
 *
 * El asistente pidió un dato de la cabecera que faltaba (el NIF, la dirección…),
 * la persona se lo dijo y `guardar_dato_cabecera` lo validó: aquí se ve QUÉ se
 * guarda, y NADA cambia hasta pulsar el botón. Guarda el navegador con la sesión
 * del admin (solo esa columna; qué sale siempre en la cabecera se decide en
 * Ajustes) y, si sale bien, el chat pide SOLO que se rehaga el informe
 * (`onGuardado`, una vez por tarjeta), como el seguimiento del cuaderno (CU7).
 *
 * Una tarjeta del historial con el NIF tapado (E11) ya no se puede guardar: se dice.
 */
export function AssistantMarcaCambioCard({ item, onGuardado }: { item: Record<string, unknown>; onGuardado?: () => void }) {
  const { t } = useTranslation();
  const { company, role } = useAuth();
  const qc = useQueryClient();
  const [estado, setEstado] = useState<Estado>("idle");
  const [resultado, setResultado] = useState<ResultadoGuardar | null>(null);
  const avisado = useRef(false);
  const tarjeta = useMemo(() => tarjetaMarca(item), [item]);

  if (!tarjeta) {
    return (
      <div data-testid="assistant-marca-cambio-invalid" className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        {t("marcaInformes.asistente.invalida")}
      </div>
    );
  }

  const esAdmin = role === "admin";
  const etiqueta = t(`marcaInformes.cabecera.campos.${tarjeta.campo}`);
  const accion = getAssistantAction("settings_marca");
  const url = accion ? buildActionUrl(accion, { values: { mode: "cabecera" } }) : null;
  const botones: AssistantActionItem[] = url ? [{ id: "marca-cabecera", label: t("marcaInformes.title"), url }] : [];

  const guardar = async () => {
    if (!company?.id || estado === "saving" || !esAdmin) return;
    setEstado("saving");
    const r = await guardarDatoCabecera(supabase, company.id, item);
    setResultado(r);
    if (!r.ok) {
      setEstado("error");
      return;
    }
    setEstado("saved");
    // Lo que enseña «Personalizar informes» (la fila y su estado).
    void qc.invalidateQueries({ queryKey: ["marca-ajustes", company.id] });
    void qc.invalidateQueries({ queryKey: ["marca-estado", company.id] });
    if (!avisado.current) {
      avisado.current = true;
      onGuardado?.();
    }
  };

  // `"motivo" in` y no `.ok`: sin strictNullChecks, TS no estrecha la unión por un booleano.
  const motivo = resultado && "motivo" in resultado ? resultado.motivo : null;
  const error =
    motivo === "permiso"
      ? t("marcaInformes.asistente.soloAdmin")
      : motivo === "invalida"
        ? t("marcaInformes.asistente.invalida")
        : motivo
          ? t("marcaInformes.errorGuardar")
          : "";

  return (
    <div data-testid="assistant-marca-cambio-card" className="mt-2 w-full rounded-2xl border border-[#b6dded] bg-white px-3 py-2.5 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#2E84DE] text-white">
          <Building2 className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("marcaInformes.asistente.titulo")}</span>
          <span data-testid="assistant-marca-cambio-dato" className="block break-words text-sm font-bold text-foreground">
            {etiqueta}: {tarjeta.valor}
          </span>
        </span>
      </div>

      {estado !== "saved" && estado !== "discarded" && (
        <p className="mt-2 text-[11px] text-muted-foreground">{t("marcaInformes.asistente.donde")}</p>
      )}

      {!esAdmin && estado === "idle" && (
        <p data-testid="assistant-marca-cambio-solo-admin" className="mt-2 text-xs text-muted-foreground">
          {t("marcaInformes.asistente.soloAdmin")}
        </p>
      )}

      {estado === "saved" && (
        <div data-testid="assistant-marca-cambio-saved">
          <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
            {t("marcaInformes.asistente.guardado")}
          </p>
          <AssistantActionButtons items={botones} />
        </div>
      )}
      {estado === "discarded" && <p className="mt-2 text-xs text-muted-foreground">{t("marcaInformes.asistente.descartado")}</p>}
      {error && estado === "error" && (
        <p data-testid="assistant-marca-cambio-error" className="mt-2 text-xs font-medium text-destructive">
          {error}
        </p>
      )}

      {esAdmin && (estado === "idle" || estado === "saving" || estado === "error") && (
        <div className="mt-2.5 flex gap-2">
          <button
            type="button"
            onClick={guardar}
            disabled={estado === "saving"}
            data-testid="assistant-marca-cambio-guardar"
            className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#2E84DE] px-3 text-[13px] font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60"
          >
            {estado === "saving" && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("marcaInformes.asistente.guardar")}
          </button>
          <button
            type="button"
            onClick={() => setEstado("discarded")}
            disabled={estado === "saving"}
            data-testid="assistant-marca-cambio-descartar"
            className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-xl border border-border px-3 text-[13px] font-semibold text-foreground transition-all active:scale-[0.98] disabled:opacity-60"
          >
            {t("marcaInformes.asistente.descartar")}
          </button>
        </div>
      )}
    </div>
  );
}
